import React from 'react';
import type { GasSupplyZoneCommitment } from '../../lib/api';

interface Props {
  zone: GasSupplyZoneCommitment;
  suppressPrecedent?: boolean;
}

const MW_PER_MMCF = 5.07;

const formatGw = (bcfd: number | null): string | null => {
  if (bcfd == null) return null;
  const gw = bcfd * MW_PER_MMCF;
  return gw >= 1 ? `${gw.toFixed(1)} GW` : `${Math.round(gw * 1000)} MW`;
};

const formatAcres = (n: number | null): string | null => {
  if (n == null) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M ac`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k ac`;
  return `${n.toLocaleString()} ac`;
};

// Per-zone bespoke copy. Lifted verbatim from v1's OperatorSupplySection.tsx.
// Slug-keyed so adding a new zone (e.g. Pacifico, Crusoe) is a one-place edit.
// Future move: into operator_commitments.narrative_blocks JSONB if the count grows.
const ZONE_NARRATIVE: Record<string, {
  precedent?: { headline: string; body: string };
  team?: { headline: string; body: string };
}> = {
  'revenant-east-tx': {
    team: {
      headline: 'Aethon Energy alumni',
      body:
        "Revenant's leadership are Aethon Energy alumni. The same operating team has executed multi-Bcf/d gas dedications at scale before; track record is the de-risk on the Phase 1 deliverability date.",
    },
    precedent: {
      headline: 'In-AOI precedent: Project Lufkin (Amp Z, up to 2.1 GW hybrid)',
      body:
        "Amp Z's $1.05B AI-campus build sits inside this AOI: grid-first (1.1 GW grid-connected by 2028) with ~1 GW of on-site gas self-generation layered on top by 2029 — a hybrid, not a pure BTM build, and its TCEQ and ERCOT processes are still in flight. What it proves is that gigawatt-scale compute capital has already chosen this county. Land-pricing implication: AOI is still at commodity timber comps; the local re-pricing happened after the Amp Z announcement. Lock counterparties before public announcement, not after.",
    },
  },
};

export const OperatorSupplyStoryV2: React.FC<Props> = ({ zone, suppressPrecedent }) => {
  const gwLabel = formatGw(zone.peakBcfd);
  const acLabel = formatAcres(zone.dedicatedAcres);
  const inventoryLabel = zone.inventoryYears ? `${zone.inventoryYears}-yr inventory` : null;
  const corridor = zone.corridorTakeaway;
  const narrative = ZONE_NARRATIVE[zone.slug] || {};

  // Hero stat strip. 3 cells, each earns its place. Peak supply is emphasized;
  // operator footprint (acres) is left to the lede so the strip stays scannable.
  const heroCells: Array<{ value: string; label: string; emphasis?: boolean }> = [];
  if (gwLabel) heroCells.push({ value: gwLabel, label: `peak supply${zone.peakYear ? ` by ${zone.peakYear}` : ''}`, emphasis: true });
  if (zone.inventoryYears) heroCells.push({ value: `${zone.inventoryYears}-yr`, label: 'drilling inventory' });
  if (corridor && corridor.totalBcfd != null) {
    heroCells.push({
      value: `${corridor.totalBcfd.toFixed(1)} Bcf/d`,
      // Sum of rated capacities across the corridor's systems — regional
      // infrastructure context, not additive locally-available takeaway.
      label: `corridor systems · ${corridor.pipelineCount} lines (context)`,
    });
  }

  return (
    <div className="v2-supply-story">
      <div className="v2-supply-eyebrow">Operator supply story</div>
      <h3 className="v2-supply-title">The {zone.operatorName} supply story</h3>

      <p className="v2-supply-lede">
        This parcel sits inside {zone.operatorName}’s{' '}
        {zone.assetName ? <em>{zone.assetName}</em> : 'gas supply footprint'}
        {zone.regionLabel ? ` in ${zone.regionLabel}` : ''}, a planned{' '}
        {gwLabel ? <strong>{gwLabel}</strong> : 'multi-GW'} of peak behind-the-meter supply
        {zone.peakYear ? ` by ${zone.peakYear}` : ''}
        {acLabel ? `, drawn from ${acLabel} of operator-controlled acreage` : ''}
        {zone.inventoryYears ? ` and a ${zone.inventoryYears}-year drilling inventory` : ''}.
        {' '}{zone.operatorName} has indicated they will build operator gathering to qualified BTM buyers; public midstream lines are redundancy, not the firm path.
      </p>

      {heroCells.length > 0 && (
        <div className="v2-supply-hero" data-cells={heroCells.length}>
          {heroCells.map((cell, i) => (
            <div key={i} className={`v2-supply-cell${cell.emphasis ? ' emphasis' : ''}`}>
              <div className="v2-supply-cell-value">{cell.value}</div>
              <div className="v2-supply-cell-label">{cell.label}</div>
            </div>
          ))}
        </div>
      )}

      {narrative.team && (
        <div className="v2-supply-block">
          <div className="v2-supply-block-label">{narrative.team.headline}</div>
          <p className="v2-supply-block-body">{narrative.team.body}</p>
        </div>
      )}

      {narrative.precedent && !suppressPrecedent && (
        <div className="v2-supply-block">
          <div className="v2-supply-block-label">{narrative.precedent.headline}</div>
          <p className="v2-supply-block-body">{narrative.precedent.body}</p>
        </div>
      )}

      {zone.drillScheduleNotes && (
        <div className="v2-supply-drill">
          <div className="v2-supply-block-label muted">Drill schedule</div>
          <p className="v2-supply-drill-body">{zone.drillScheduleNotes}</p>
        </div>
      )}
    </div>
  );
};
